import React, { useState, useEffect } from 'react';
import {
    Mail, HelpCircle, FileText, CheckCircle, Trash2, Save, LogOut,
    Plus, Loader, Shield, Lock, Layout, Star, Wifi, Car, Coffee, Tv, Wind,
    Utensils, MapPin, Smile, X, Quote, Home, ClipboardList, BookOpen, Eye, EyeOff, Edit3,
    ShieldCheck, Upload, ExternalLink, BookText, Copy, KeyRound, RefreshCw, UserPlus
} from 'lucide-react';
import api from '../utils/api';
import OnboardingAdminPanel from '../components/onboarding/OnboardingAdminPanel';
import {
    changePassword, adminGetAllBlogPosts, adminSaveBlogPost, adminDeleteBlogPost, certifyBlogPost, uploadBlogImage,
    adminListManuals, adminCreateManual, adminResetManualPassword, adminRegenerateManualSlugs, adminDeleteManual,
    adminGetManualFull, adminSaveManual, adminUploadManualVideo, adminUploadManualImage
} from '../services/apiService';
import {BlogPost, ContactMessage, FaqItem, PropertySubmission, SiteSettings, Testimonial, HouseManualAdmin, HouseManual} from '../types';
import BlogBlockEditor, { ContentBlock, genId, parseContentToBlocks } from '../components/BlogBlockEditor';
import ManualEditorForm from '../components/ManualEditorForm';
import { useNavigate } from 'react-router-dom';

// Available icons for services
const SERVICE_ICONS = [
    { name: 'Wifi', icon: <Wifi className="w-5 h-5"/> },
    { name: 'Car', icon: <Car className="w-5 h-5"/> },
    { name: 'Coffee', icon: <Coffee className="w-5 h-5"/> },
    { name: 'Tv', icon: <Tv className="w-5 h-5"/> },
    { name: 'Air Conditioning', icon: <Wind className="w-5 h-5"/> },
    { name: 'Dining', icon: <Utensils className="w-5 h-5"/> },
    { name: 'Location', icon: <MapPin className="w-5 h-5"/> },
    { name: 'Hospitality', icon: <Smile className="w-5 h-5"/> },
    { name: 'Special', icon: <Star className="w-5 h-5"/> },
];

interface ServiceItem {
    id: string; // Using timestamp as temporary ID
    iconName: string;
    title: string;
    description: string;
}

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'messages' | 'faqs' | 'content' | 'security' | 'testimonials' | 'submissions' | 'blog' | 'manuals' | 'onboarding'>('messages');
    const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
    const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
    const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
    const EMPTY_POST: BlogPost = { title: '', slug: '', excerpt: '', content: '', author: '', coverImageUrl: '', category: '', published: false };
    const [certifyingId, setCertifyingId] = useState<number | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);

    // Data States
    const [messages, setMessages] = useState<ContactMessage[]>([]);
    const [faqs, setFaqs] = useState<FaqItem[]>([]);
    const [settings, setSettings] = useState<SiteSettings>({});
    const [submissions, setSubmissions] = useState<PropertySubmission[]>([]);
    const [manuals, setManuals] = useState<HouseManualAdmin[]>([]);
    const [newManual, setNewManual] = useState({ houseName: '', address: '', badge: '', ownerPassword: '' });
    const [creatingManual, setCreatingManual] = useState(false);
    const [resetPwdId, setResetPwdId] = useState<number | null>(null);
    const [resetPwdValue, setResetPwdValue] = useState('');
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [editingManualId, setEditingManualId] = useState<number | null>(null);
    const [editingManual, setEditingManual] = useState<HouseManual | null>(null);
    const [editingManualLoading, setEditingManualLoading] = useState(false);

    // Password State
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // Custom Services State
    const [customServices, setCustomServices] = useState<ServiceItem[]>([]);

    // UI State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    // Initial Data Fetch on Tab Change
    useEffect(() => {
        // 'security' and 'onboarding' panels manage their own data internally.
        if (activeTab !== 'security' && activeTab !== 'onboarding') {
            fetchData();
        }
    }, [activeTab]);

    // Parsing Service JSON when settings arrive
    useEffect(() => {
        if (settings.services_list) {
            try {
                const parsed = JSON.parse(settings.services_list);
                if (Array.isArray(parsed)) {
                    setCustomServices(parsed);
                }
            } catch (e) {
                console.error("Error parsing services", e);
                setCustomServices([]);
            }
        }
    }, [settings.services_list]);

    // Clear feedback after 3 seconds
    useEffect(() => {
        if (feedback) {
            const timer = setTimeout(() => setFeedback(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [feedback]);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'messages') {
                const res = await api.get('/content/admin/messages');
                if(res.data.success) setMessages(res.data.data);
            } else if (activeTab === 'faqs') {
                const res = await api.get('/content/faqs');
                if(res.data.success) setFaqs(res.data.data);
            } else if (activeTab === 'content') {
                const res = await api.get('/content/settings');
                if(res.data.success) setSettings(res.data.data);
            } else if (activeTab === 'testimonials') {
                // Endpoint ipotetico backend
                const res = await api.get('/content/testimonials');
                if(res.data.success) setTestimonials(res.data.data);
            } else if (activeTab === 'submissions') {
                const res = await api.get('/content/property-submissions');
                if(res.data.success) setSubmissions(res.data.data);
            } else if (activeTab === 'blog') {
                const data = await adminGetAllBlogPosts();
                if (data) setBlogPosts(data);
            } else if (activeTab === 'manuals') {
                const data = await adminListManuals();
                if (data) setManuals(data);
            }
        } catch (error) {
            console.error("Admin fetch error", error);
        } finally {
            setLoading(false);
        }
    };

    const handleTestimonialChange = (index: number, field: keyof Testimonial, value: any) => {
        const newTestimonials = [...testimonials];
        newTestimonials[index] = { ...newTestimonials[index], [field]: value };
        setTestimonials(newTestimonials);
    };

    const addTestimonial = () => {
        // Aggiunge un template vuoto
        setTestimonials([...testimonials, {
            name: '',
            role: 'Property Owner',
            property: '',
            preview: '',
            fullText: '',
            rating: 5
        }]);
    };

    const saveTestimonial = async (t: Testimonial) => {
        try {
            setSaving(true);
            // Endpoint ipotetico per salvare/aggiornare
            await api.post('/content/admin/testimonials', t);
            setFeedback({ type: 'success', message: 'Testimonial saved successfully!' });
            fetchData(); // Ricarica per avere l'ID corretto
        } catch (error) {
            setFeedback({ type: 'error', message: 'Error saving testimonial.' });
        } finally {
            setSaving(false);
        }
    };

    const deleteTestimonial = async (id?: number) => {
        if(!id) return; // Se non ha ID (è nuovo e non salvato), potresti rimuoverlo solo dallo stato locale
        if(!window.confirm("Are you sure?")) return;

        try {
            await api.delete(`/content/admin/testimonials/${id}`);
            setTestimonials(prev => prev.filter(t => t.id !== id));
            setFeedback({ type: 'success', message: 'Testimonial deleted.' });
        } catch (error) {
            setFeedback({ type: 'error', message: 'Error deleting testimonial.' });
        }
    };

    // --- MESSAGE LOGIC ---
    const markAsRead = async (id: number) => {
        try {
            await api.put(`/content/admin/messages/${id}/read`);
            setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
        } catch (error) {
            console.error("Error updating message", error);
        }
    };

    // --- FAQ LOGIC ---
    const handleFaqChange = (index: number, field: keyof FaqItem, value: string) => {
        const newFaqs = [...faqs];
        newFaqs[index] = { ...newFaqs[index], [field]: value };
        setFaqs(newFaqs);
    };

    const addFaq = () => {
        setFaqs([...faqs, { question: '', answer: '' }]);
    };

    const saveFaq = async (faq: FaqItem) => {
        try {
            setSaving(true);
            await api.post('/content/admin/faqs', faq);
            setFeedback({ type: 'success', message: 'FAQ saved successfully!' });
            fetchData();
        } catch (error) {
            setFeedback({ type: 'error', message: 'Error saving the FAQ.' });
        } finally {
            setSaving(false);
        }
    };

    const deleteFaq = async (id?: number) => {
        if(!id) return;
        if(!window.confirm("Are you sure you want to delete this FAQ?")) return;

        try {
            await api.delete(`/content/admin/faqs/${id}`);
            setFaqs(prev => prev.filter(f => f.id !== id));
            setFeedback({ type: 'success', message: 'FAQ deleted.' });
        } catch (error) {
            setFeedback({ type: 'error', message: 'Error during deletion.' });
        }
    };

    // --- CONTENT & SERVICES LOGIC ---
    const handleSettingChange = (key: string, value: string) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const addService = () => {
        const newService: ServiceItem = {
            id: Date.now().toString(),
            iconName: 'Special',
            title: '',
            description: ''
        };
        setCustomServices([...customServices, newService]);
    };

    const updateService = (id: string, field: keyof ServiceItem, value: string) => {
        setCustomServices(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
    };

    const deleteService = (id: string) => {
        setCustomServices(prev => prev.filter(s => s.id !== id));
    };

    const saveSettings = async () => {
        try {
            setSaving(true);
            const settingsToSave = { ...settings };
            settingsToSave.services_list = JSON.stringify(customServices);

            const settingsArray = Object.keys(settingsToSave).map(key => ({
                keyName: key,
                value: settingsToSave[key as keyof SiteSettings]
            }));

            await api.post('/content/admin/settings', settingsArray);
            setFeedback({ type: 'success', message: 'Settings updated successfully!' });
        } catch (error) {
            setFeedback({ type: 'error', message: 'Error saving settings.' });
        } finally {
            setSaving(false);
        }
    };

    // --- SECURITY LOGIC (PASSWORD) ---
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const submitPasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setFeedback({ type: 'error', message: 'The new passwords do not match.' });
            return;
        }

        setSaving(true);
        try {
            const res = await changePassword(passwordData);
            if (res.success) {
                setFeedback({ type: 'success', message: 'Password changed successfully!' });
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            } else {
                setFeedback({ type: 'error', message: res.message || 'Error changing password.' });
            }
        } catch (error: any) {
            setFeedback({ type: 'error', message: error.response?.data?.message || 'Server error.' });
        } finally {
            setSaving(false);
        }
    };

    // --- BLOG LOGIC ---
    const saveBlogPost = async (post: BlogPost) => {
        try {
            setSaving(true);
            await adminSaveBlogPost(post);
            setFeedback({ type: 'success', message: 'Post saved successfully!' });
            setEditingPost(null);
            const data = await adminGetAllBlogPosts();
            if (data) setBlogPosts(data);
        } catch {
            setFeedback({ type: 'error', message: 'Error saving the post.' });
        } finally {
            setSaving(false);
        }
    };

    const deleteBlogPost = async (id?: number) => {
        if (!id) return;
        if (!window.confirm('Delete this post?')) return;
        try {
            await adminDeleteBlogPost(id);
            setBlogPosts(prev => prev.filter(p => p.id !== id));
            if (editingPost?.id === id) setEditingPost(null);
            setFeedback({ type: 'success', message: 'Post deleted.' });
        } catch {
            setFeedback({ type: 'error', message: 'Error deleting the post.' });
        }
    };

    const handleCertifyPost = async (id?: number) => {
        if (!id) return;
        if (!window.confirm('Certify this article on the Solana blockchain via Axiomgroup? This will consume one certification credit.')) return;
        setCertifyingId(id);
        try {
            const res = await certifyBlogPost(id);
            if (res.success) {
                setFeedback({ type: 'success', message: 'Article certified on blockchain!' });
                const data = await adminGetAllBlogPosts();
                if (data) setBlogPosts(data);
            } else {
                setFeedback({ type: 'error', message: res.message || 'Certification failed.' });
            }
        } catch {
            setFeedback({ type: 'error', message: 'Certification API error. Check the certification server.' });
        } finally {
            setCertifyingId(null);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !editingPost) return;
        setUploadingImage(true);
        try {
            const res = await uploadBlogImage(file);
            if (res.success) {
                setEditingPost({ ...editingPost, coverImageUrl: res.data });
                setFeedback({ type: 'success', message: 'Image uploaded!' });
            }
        } catch {
            setFeedback({ type: 'error', message: 'Image upload failed.' });
        } finally {
            setUploadingImage(false);
        }
    };

    // --- MANUALS LOGIC ---
    const createManual = async () => {
        if (!newManual.houseName.trim() || !newManual.address.trim() || !newManual.ownerPassword.trim()) {
            setFeedback({ type: 'error', message: 'House name, address and owner password are required.' });
            return;
        }
        if (newManual.ownerPassword.length < 4) {
            setFeedback({ type: 'error', message: 'Owner password must be at least 4 characters.' });
            return;
        }
        setCreatingManual(true);
        try {
            const res = await adminCreateManual(newManual);
            if (res.success) {
                setFeedback({ type: 'success', message: 'House manual created.' });
                setNewManual({ houseName: '', address: '', badge: '', ownerPassword: '' });
                fetchData();
            } else {
                setFeedback({ type: 'error', message: res.message || 'Creation failed.' });
            }
        } catch {
            setFeedback({ type: 'error', message: 'Server error.' });
        } finally {
            setCreatingManual(false);
        }
    };

    const deleteManualById = async (id: number) => {
        if (!window.confirm('Delete this house manual? Videos will remain on storage.')) return;
        try {
            await adminDeleteManual(id);
            setManuals(prev => prev.filter(m => m.id !== id));
            setFeedback({ type: 'success', message: 'Manual deleted.' });
        } catch {
            setFeedback({ type: 'error', message: 'Error deleting.' });
        }
    };

    const submitResetPassword = async (id: number) => {
        if (resetPwdValue.length < 4) {
            setFeedback({ type: 'error', message: 'New password must be at least 4 characters.' });
            return;
        }
        try {
            const res = await adminResetManualPassword(id, resetPwdValue);
            if (res.success) {
                setFeedback({ type: 'success', message: 'Owner password updated.' });
                setResetPwdId(null);
                setResetPwdValue('');
            } else {
                setFeedback({ type: 'error', message: res.message || 'Reset failed.' });
            }
        } catch {
            setFeedback({ type: 'error', message: 'Server error.' });
        }
    };

    const regenerateSlugs = async (id: number) => {
        if (!window.confirm('Regenerate links? Old links will stop working immediately.')) return;
        try {
            const res = await adminRegenerateManualSlugs(id);
            if (res.success) {
                setFeedback({ type: 'success', message: 'Links regenerated.' });
                fetchData();
            }
        } catch {
            setFeedback({ type: 'error', message: 'Server error.' });
        }
    };

    const buildGuestUrl = (slug: string) => `${window.location.origin}/manual/${slug}`;
    const buildEditUrl = (slug: string) => `${window.location.origin}/manual/edit/${slug}`;

    const openManualEditor = async (id: number) => {
        setEditingManualId(id);
        setEditingManualLoading(true);
        try {
            const data = await adminGetManualFull(id);
            if (data) {
                setEditingManual({
                    houseName: data.houseName || '',
                    address: data.address || '',
                    badge: data.badge || '',
                    welcomeMessage: data.welcomeMessage || '',
                    assistancePhone: data.assistancePhone || '',
                    checkinVideoUrl: data.checkinVideoUrl || '',
                    intercomCode: data.intercomCode || '',
                    keyBoxCode: data.keyBoxCode || '',
                    wifiName: data.wifiName || '',
                    wifiPassword: data.wifiPassword || '',
                    items: (data.items || []).map((it: any) => ({
                        id: it.id, title: it.title || '', instructions: it.instructions || '',
                        videoUrl: it.videoUrl || '', content: it.content || '', displayOrder: it.displayOrder
                    }))
                });
            }
        } catch {
            setFeedback({ type: 'error', message: 'Error loading manual.' });
        } finally {
            setEditingManualLoading(false);
        }
    };

    const closeManualEditor = () => {
        setEditingManualId(null);
        setEditingManual(null);
    };

    const saveEditingManual = async () => {
        if (editingManualId == null || !editingManual) return;
        setSaving(true);
        try {
            const res = await adminSaveManual(editingManualId, editingManual);
            if (res.success) {
                setFeedback({ type: 'success', message: 'Manual saved.' });
                if (res.data) {
                    setEditingManual(prev => prev ? {
                        ...prev,
                        items: (res.data.items || []).map((it: any) => ({
                            id: it.id, title: it.title || '', instructions: it.instructions || '',
                            videoUrl: it.videoUrl || '', content: it.content || '', displayOrder: it.displayOrder
                        }))
                    } : prev);
                }
                fetchData();
            } else {
                setFeedback({ type: 'error', message: res.message || 'Save failed.' });
            }
        } catch {
            setFeedback({ type: 'error', message: 'Server error.' });
        } finally {
            setSaving(false);
        }
    };

    const uploadEditingVideo = async (blob: Blob, onProgress: (pct: number) => void): Promise<string> => {
        if (editingManualId == null) throw new Error('No manual selected');
        const res = await adminUploadManualVideo(editingManualId, blob, onProgress);
        if (res.success) return res.data as string;
        throw new Error(res.message || 'Upload failed');
    };

    const uploadEditingImage = async (file: File): Promise<string> => {
        if (editingManualId == null) throw new Error('No manual selected');
        const res = await adminUploadManualImage(editingManualId, file);
        if (res.success) return res.data as string;
        throw new Error(res.message || 'Upload failed');
    };

    const copyToClipboard = (text: string, key: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(prev => prev === key ? null : prev), 1500);
        });
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        navigate('/login');
        window.location.reload();
    };

    const renderIcon = (name: string) => {
        const found = SERVICE_ICONS.find(i => i.name === name);
        return found ? found.icon : <Star className="w-5 h-5"/>;
    };

    return (
        <div className="min-h-screen bg-gray-100 flex font-sans">

            {/* SIDEBAR */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col fixed h-full z-10 shadow-lg">
                <div className="p-6 border-b border-gray-100">
                    <h2 className="text-2xl font-serif font-bold text-brand">Admin Panel</h2>
                    <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider font-bold">Site Management</p>
                </div>

                <nav className="flex-1 p-4 space-y-2">
                    <button onClick={() => setActiveTab('messages')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'messages' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <Mail className="w-5 h-5"/> Messages
                    </button>
                    <button onClick={() => setActiveTab('faqs')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'faqs' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <HelpCircle className="w-5 h-5"/> FAQs
                    </button>
                    <button onClick={() => setActiveTab('content')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'content' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <Layout className="w-5 h-5"/> Site Content
                    </button>
                    <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'security' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <Shield className="w-5 h-5"/> Security
                    </button>
                    <button onClick={() => setActiveTab('testimonials')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'testimonials' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <Quote className="w-5 h-5"/> Testimonials
                    </button>
                    <button onClick={() => setActiveTab('submissions')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'submissions' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <ClipboardList className="w-5 h-5"/> Submissions
                    </button>
                    <button onClick={() => { setActiveTab('blog'); setEditingPost(null); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'blog' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <BookOpen className="w-5 h-5"/> Blog
                    </button>
                    <button onClick={() => setActiveTab('manuals')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'manuals' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <BookText className="w-5 h-5"/> House Manuals
                    </button>
                    <button onClick={() => setActiveTab('onboarding')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-medium ${activeTab === 'onboarding' ? 'bg-brand text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}>
                        <UserPlus className="w-5 h-5"/> Owner Onboarding
                    </button>
                </nav>

                <div className="p-4 border-t border-gray-100 space-y-2">

                    {/* NUOVO TASTO BACK TO WEBSITE */}
                    <button onClick={() => navigate('/')} className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-xl w-full transition-colors font-medium">
                        <Home className="w-5 h-5"/> Back to Website
                    </button>

                    {/* TASTO LOGOUT ESISTENTE */}
                    <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl w-full transition-colors font-medium">
                        <LogOut className="w-5 h-5"/> Logout
                    </button>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="flex-1 ml-64 p-10 overflow-y-auto min-h-screen">
                <div className="max-w-5xl mx-auto">
                    <h1 className="text-4xl font-bold text-gray-900 mb-8 capitalize font-serif border-b pb-4">
                        {activeTab === 'content' ? 'Site Content' :
                            activeTab === 'security' ? 'Account Security' :
                                activeTab === 'messages' ? 'Received Messages' :
                                    activeTab === 'submissions' ? 'All Submissions' :
                                        activeTab === 'blog' ? 'Blog Management' :
                                            activeTab === 'manuals' ? 'House Manuals' :
                                                activeTab === 'onboarding' ? 'Owner Onboarding' :
                                                    activeTab === 'testimonials' ? 'Testimonials' : 'Frequently Asked Questions (FAQ)'}
                    </h1>

                    {/* FEEDBACK ALERT */}
                    {feedback && (
                        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 text-sm font-bold ${feedback.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                            {feedback.type === 'success' ? <CheckCircle className="w-5 h-5"/> : <Trash2 className="w-5 h-5"/>}
                            {feedback.message}
                        </div>
                    )}

                    {loading && activeTab !== 'security' && activeTab !== 'onboarding' ? (
                        <div className="flex items-center justify-center py-20 text-gray-400">
                            <Loader className="w-8 h-8 animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* --- MESSAGES TAB --- */}
                            {activeTab === 'messages' && (
                                <div className="space-y-4">
                                    {messages.map(msg => (
                                        <div key={msg.id} className={`bg-white p-6 rounded-2xl border transition-all ${msg.read ? 'border-gray-200 bg-gray-50/50' : 'border-l-4 border-l-brand shadow-sm bg-white'}`}>
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${msg.read ? 'bg-gray-400' : 'bg-brand'}`}>
                                                        {msg.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-bold text-lg text-gray-900">{msg.name}</h3>
                                                        <p className="text-sm text-gray-500 font-medium">{msg.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end">
                                                    <span className="text-xs text-gray-400 font-medium mb-2">{new Date(msg.createdAt).toLocaleDateString()}</span>
                                                    {!msg.read && (
                                                        <button onClick={() => markAsRead(msg.id)} className="flex items-center gap-1 text-xs font-bold text-brand hover:bg-brand/10 px-2 py-1 rounded-lg transition-colors">
                                                            <CheckCircle className="w-4 h-4"/> Mark as Read
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="pl-13 ml-13">
                                                <p className="text-gray-700 bg-gray-50 p-4 rounded-xl border border-gray-100 text-sm leading-relaxed">{msg.message}</p>
                                            </div>
                                        </div>
                                    ))}
                                    {messages.length === 0 && (
                                        <div className="text-center py-20 text-gray-400">
                                            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50"/>
                                            <p className="font-medium">No messages received yet.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* --- FAQ TAB --- */}
                            {activeTab === 'faqs' && (
                                <div className="space-y-6">
                                    {faqs.map((faq, index) => (
                                        <div key={index} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-6 items-start group hover:border-brand/30 transition-colors">
                                            <div className="flex-1 space-y-4 w-full">
                                                <div className="relative">
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Question</label>
                                                    <input
                                                        type="text"
                                                        value={faq.question}
                                                        onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                                                        className="w-full font-bold text-lg text-gray-800 border-b border-gray-200 focus:border-brand outline-none pb-2 bg-transparent"
                                                        placeholder="Enter the question..."
                                                    />
                                                </div>
                                                <div className="relative">
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Answer</label>
                                                    <textarea
                                                        value={faq.answer}
                                                        onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                                                        className="w-full text-gray-600 bg-gray-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-brand/20 outline-none text-sm leading-relaxed resize-y"
                                                        rows={3}
                                                        placeholder="Enter the answer..."
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex flex-row md:flex-col gap-2 pt-6">
                                                <button onClick={() => saveFaq(faq)} disabled={saving} className="p-3 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-colors shadow-sm" title="Save">
                                                    <Save className="w-5 h-5"/>
                                                </button>
                                                <button onClick={() => deleteFaq(faq.id)} className="p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors shadow-sm" title="Delete">
                                                    <Trash2 className="w-5 h-5"/>
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    <button onClick={addFaq} className="w-full py-6 border-2 border-dashed border-gray-300 rounded-2xl text-gray-400 hover:border-brand hover:text-brand hover:bg-brand/5 font-bold transition-all flex flex-col items-center gap-2">
                                        <Plus className="w-8 h-8" />
                                        Add New FAQ
                                    </button>
                                </div>
                            )}

                            {activeTab === 'submissions' && (
                                <div className="space-y-6">
                                    {submissions.map((sub) => (
                                        <div key={sub.id} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm transition-all hover:shadow-md">
                                            {/* HEADER CARD */}
                                            <div className="flex justify-between items-start mb-6 border-b border-gray-100 pb-4">
                                                <div>
                                                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                                        <Home className="w-5 h-5 text-brand" />
                                                        {sub.address}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        Owner: <span className="font-bold text-gray-800">{sub.ownerName}</span>
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <span className="bg-brand/10 text-brand text-xs font-bold px-3 py-1 rounded-full">
                                                        NEW SUBMISSION
                                                    </span>
                                                    <p className="text-xs text-gray-400 mt-2">
                                                        {new Date(sub.createdAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm text-gray-700">

                                                {/* COLONNA 1: INFO GENERALI */}
                                                <div className="space-y-4">
                                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 h-full">
                                                        <h4 className="font-bold text-brand uppercase text-xs mb-3 flex items-center gap-2">
                                                            <FileText className="w-4 h-4"/> Property Details
                                                        </h4>
                                                        <ul className="space-y-2">
                                                            <li><strong className="text-gray-900">Unit Type:</strong> {sub.unitType}</li>
                                                            <li><strong className="text-gray-900">Property Type:</strong> {sub.propertyType}</li>
                                                            <li><strong className="text-gray-900">Rooms:</strong> {sub.roomDetails}</li>
                                                            <li><strong className="text-gray-900">Occupancy:</strong> {sub.occupancy} Guests</li>
                                                            <li><strong className="text-gray-900">Size:</strong> {sub.dimensions}</li>
                                                            <li><strong className="text-gray-900">License:</strong> {sub.licenseNumber}</li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                {/* COLONNA 2: OPERAZIONI & TECH */}
                                                <div className="space-y-4">
                                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 h-full">
                                                        <h4 className="font-bold text-brand uppercase text-xs mb-3 flex items-center gap-2">
                                                            <Wifi className="w-4 h-4"/> Ops & Tech
                                                        </h4>
                                                        <ul className="space-y-3">
                                                            <li className="bg-white p-2 rounded border border-gray-200">
                                                                <span className="block text-[10px] uppercase font-bold text-gray-400">Wifi</span>
                                                                <div className="flex justify-between">
                                                                    <span>{sub.wifiName}</span>
                                                                    <span className="font-mono bg-gray-100 px-1 rounded">{sub.wifiPassword}</span>
                                                                </div>
                                                            </li>
                                                            <li>
                                                                <strong className="text-gray-900 block mb-1">House Manual:</strong>
                                                                <p className="text-xs text-gray-500 italic truncate">{sub.houseManual || 'N/A'}</p>
                                                            </li>
                                                            <li>
                                                                <strong className="text-gray-900 block mb-1">Instructions:</strong>
                                                                <div className="grid grid-cols-2 gap-2 text-xs">
                                                                    <div className="bg-white p-2 rounded">
                                                                        <span className="block font-bold text-gray-400">Cleaning</span>
                                                                        {sub.cleaningInstructions || '-'}
                                                                    </div>
                                                                    <div className="bg-white p-2 rounded">
                                                                        <span className="block font-bold text-gray-400">Waste</span>
                                                                        {sub.wasteInstructions || '-'}
                                                                    </div>
                                                                    <div className="bg-white p-2 rounded col-span-2">
                                                                        <span className="block font-bold text-gray-400">Parking</span>
                                                                        {sub.parkingInstructions || '-'}
                                                                    </div>
                                                                </div>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>

                                                {/* COLONNA 3: CONTENUTO LISTING */}
                                                <div className="space-y-4">
                                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 h-full max-h-[300px] overflow-y-auto custom-scrollbar">
                                                        <h4 className="font-bold text-brand uppercase text-xs mb-3 flex items-center gap-2">
                                                            <Layout className="w-4 h-4"/> Content Preview
                                                        </h4>
                                                        <div className="space-y-3">
                                                            <div>
                                                                <span className="block text-[10px] uppercase font-bold text-gray-400">Title</span>
                                                                <p className="font-bold leading-tight">{sub.title}</p>
                                                            </div>
                                                            <div>
                                                                <span className="block text-[10px] uppercase font-bold text-gray-400">Summary</span>
                                                                <p className="text-xs text-gray-600">{sub.summary}</p>
                                                            </div>
                                                            <div className="pt-2 border-t border-gray-200">
                                                                <span className="block text-[10px] uppercase font-bold text-gray-400">Amenities</span>
                                                                <p className="text-xs text-gray-600">{sub.amenities}</p>
                                                            </div>
                                                            <div>
                                                                <span className="block text-[10px] uppercase font-bold text-gray-400">Notes</span>
                                                                <p className="text-xs text-red-500 font-medium">{sub.notes || 'No internal notes'}</p>
                                                            </div>
                                                            {/* Puoi aggiungere qui Space, Guest Access ecc. se necessario */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {submissions.length === 0 && (
                                        <div className="text-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                                            <ClipboardList className="w-16 h-16 mx-auto mb-4 text-gray-300"/>
                                            <h3 className="text-lg font-bold text-gray-900">No submissions yet</h3>
                                            <p className="text-gray-500">Property forms submitted by owners will appear here.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* --- CONTENT TAB --- */}
                            {activeTab === 'content' && (
                                <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-200">
                                    <div className="grid grid-cols-1 gap-12">

                                        {/* ABOUT US */}
                                        <div className="space-y-6 pb-8 border-b border-gray-100">
                                            <h3 className="font-serif font-bold text-2xl text-primary flex items-center gap-3">
                                                <FileText className="w-6 h-6 text-brand"/> "About Us" Section
                                            </h3>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Main Hero Title</label>
                                                    <input
                                                        type="text"
                                                        value={settings.about_us_title || ''}
                                                        onChange={(e) => handleSettingChange('about_us_title', e.target.value)}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all font-bold"
                                                        placeholder="e.g., Our Story"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Split Section Title</label>
                                                    <input
                                                        type="text"
                                                        value={settings.about_us_subtitle || ''}
                                                        onChange={(e) => handleSettingChange('about_us_subtitle', e.target.value)}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                        placeholder="e.g., Bridging technology and comfort."
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2">Split Section Content (Description)</label>
                                                <textarea
                                                    rows={4}
                                                    value={settings.about_us_description || ''}
                                                    onChange={(e) => handleSettingChange('about_us_description', e.target.value)}
                                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all text-gray-600"
                                                    placeholder="Detailed description for the split section..."
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2">Mission/Philosophy Quote</label>
                                                <textarea
                                                    rows={3}
                                                    value={settings.about_us_text || ''}
                                                    onChange={(e) => handleSettingChange('about_us_text', e.target.value)}
                                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all italic text-gray-600"
                                                    placeholder="This will appear inside the red box..."
                                                />
                                            </div>
                                        </div>

                                        {/* ADVANCED SERVICES */}
                                        <div className="space-y-6 pb-8 border-b border-gray-100">
                                            <h3 className="font-serif font-bold text-2xl text-primary flex items-center gap-3"><CheckCircle className="w-6 h-6 text-brand"/> "Services" Section</h3>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Page Title (e.g., Rates and Services)</label>
                                                    <input
                                                        type="text"
                                                        value={settings.services_title || ''}
                                                        onChange={(e) => handleSettingChange('services_title', e.target.value)}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all font-serif font-bold"
                                                        placeholder="e.g., Rates and Services"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Subtitle / Introduction</label>
                                                    <input
                                                        type="text"
                                                        value={settings.services_text || ''}
                                                        onChange={(e) => handleSettingChange('services_text', e.target.value)}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                        placeholder="e.g., We offer transparency and quality..."
                                                    />
                                                </div>
                                            </div>

                                            <div className="mt-8">
                                                <h4 className="font-bold text-lg text-gray-800 mb-4">Service Card List</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    {customServices.map((service, idx) => (
                                                        <div key={service.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50 relative group">
                                                            <button
                                                                onClick={() => deleteService(service.id)}
                                                                className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors p-1"
                                                            >
                                                                <X className="w-4 h-4"/>
                                                            </button>

                                                            <div className="flex items-center gap-4 mb-4">
                                                                <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm text-brand">
                                                                    {renderIcon(service.iconName)}
                                                                </div>
                                                                <div className="flex-1">
                                                                    <label className="text-[10px] uppercase font-bold text-gray-400">Service Title</label>
                                                                    <input
                                                                        type="text"
                                                                        value={service.title}
                                                                        onChange={(e) => updateService(service.id, 'title', e.target.value)}
                                                                        className="w-full bg-transparent border-b border-gray-300 focus:border-brand outline-none font-bold text-gray-800"
                                                                        placeholder="e.g., Free Wi-Fi"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="mb-4">
                                                                <label className="text-[10px] uppercase font-bold text-gray-400">Description</label>
                                                                <textarea
                                                                    value={service.description}
                                                                    onChange={(e) => updateService(service.id, 'description', e.target.value)}
                                                                    className="w-full bg-white border border-gray-200 rounded-lg p-2 text-sm text-gray-600 focus:border-brand outline-none"
                                                                    rows={2}
                                                                    placeholder="Brief service description..."
                                                                />
                                                            </div>

                                                            <div>
                                                                <label className="text-[10px] uppercase font-bold text-gray-400 mb-2 block">Select Icon</label>
                                                                <div className="flex gap-2 flex-wrap">
                                                                    {SERVICE_ICONS.map(icon => (
                                                                        <button
                                                                            key={icon.name}
                                                                            onClick={() => updateService(service.id, 'iconName', icon.name)}
                                                                            className={`p-2 rounded-md transition-all ${service.iconName === icon.name ? 'bg-brand text-white shadow-md' : 'bg-white text-gray-400 hover:bg-gray-200'}`}
                                                                            title={icon.name}
                                                                        >
                                                                            {icon.icon}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <button
                                                        onClick={addService}
                                                        className="border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center p-6 text-gray-400 hover:text-brand hover:border-brand hover:bg-brand/5 transition-all min-h-[250px]"
                                                    >
                                                        <Plus className="w-8 h-8 mb-2"/>
                                                        <span className="font-bold">Add New Service</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* CONTACTS */}
                                        <div className="space-y-6">
                                            <h3 className="font-serif font-bold text-2xl text-primary flex items-center gap-3"><Mail className="w-6 h-6 text-brand"/> Contact Information</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Official Email</label>
                                                    <input
                                                        type="text"
                                                        value={settings.contact_email || ''}
                                                        onChange={(e) => handleSettingChange('contact_email', e.target.value)}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                        placeholder="info@company.com"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-bold text-gray-700 mb-2">Official Phone</label>
                                                    <input
                                                        type="text"
                                                        value={settings.contact_phone || ''}
                                                        onChange={(e) => handleSettingChange('contact_phone', e.target.value)}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                        placeholder="+1 (555) 000-0000"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="pt-6 flex justify-end">
                                            <button
                                                onClick={saveSettings}
                                                disabled={saving}
                                                className="bg-brand text-white px-10 py-4 rounded-xl font-bold hover:bg-[#c42e40] transition-all shadow-lg hover:shadow-xl flex items-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                                            >
                                                {saving ? <Loader className="w-5 h-5 animate-spin"/> : <Save className="w-5 h-5"/>}
                                                Save All Changes
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* --- TESTIMONIALS TAB --- */}
                            {activeTab === 'testimonials' && (
                                <div className="space-y-8">
                                    <div className="grid grid-cols-1 gap-6">
                                        {testimonials.map((t, index) => (
                                            <div key={t.id || index} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm relative group hover:border-brand/30 transition-all">

                                                {/* Pulsante Elimina in alto a destra */}
                                                <button
                                                    onClick={() => deleteTestimonial(t.id)}
                                                    className="absolute top-4 right-4 text-gray-400 hover:text-red-500 p-2"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-5 h-5"/>
                                                </button>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    {/* Colonna Sinistra: Info Autore */}
                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Owner Name</label>
                                                            <input
                                                                type="text"
                                                                value={t.name}
                                                                onChange={(e) => handleTestimonialChange(index, 'name', e.target.value)}
                                                                className="w-full font-bold text-lg text-gray-800 border-b border-gray-200 focus:border-brand outline-none pb-2"
                                                                placeholder="Ex. Arthur V."
                                                            />
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div>
                                                                <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Role / Date</label>
                                                                <input
                                                                    type="text"
                                                                    value={t.role}
                                                                    onChange={(e) => handleTestimonialChange(index, 'role', e.target.value)}
                                                                    className="w-full text-sm text-gray-600 border-b border-gray-200 focus:border-brand outline-none pb-2"
                                                                    placeholder="Ex. Owner since 2021"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Property Name</label>
                                                                <input
                                                                    type="text"
                                                                    value={t.property}
                                                                    onChange={(e) => handleTestimonialChange(index, 'property', e.target.value)}
                                                                    className="w-full text-sm text-gray-600 border-b border-gray-200 focus:border-brand outline-none pb-2"
                                                                    placeholder="Ex. Villa Tuscany"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Rating (1-5)</label>
                                                            <div className="flex gap-2">
                                                                {[1, 2, 3, 4, 5].map(star => (
                                                                    <button
                                                                        key={star}
                                                                        onClick={() => handleTestimonialChange(index, 'rating', star)}
                                                                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${t.rating >= star ? 'bg-yellow-400 text-white' : 'bg-gray-100 text-gray-300'}`}
                                                                    >
                                                                        <Star className="w-4 h-4 fill-current"/>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Colonna Destra: Testi */}
                                                    <div className="space-y-4">
                                                        {/*<div>
                                                            <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Preview Text (Card)</label>
                                                            <textarea
                                                                rows={2}
                                                                value={t.preview}
                                                                onChange={(e) => handleTestimonialChange(index, 'preview', e.target.value)}
                                                                className="w-full bg-gray-50 rounded-xl p-3 text-sm border-transparent focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                                placeholder="Short preview..."
                                                            />
                                                        </div>*/}
                                                        <div>
                                                            <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Full Story (Modal)</label>
                                                            <textarea
                                                                rows={7}
                                                                value={t.fullText}
                                                                onChange={(e) => handleTestimonialChange(index, 'fullText', e.target.value)}
                                                                className="w-full bg-gray-50 rounded-xl p-3 text-sm border-transparent focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                                placeholder="Full testimonial text..."
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                                                    <button
                                                        onClick={() => saveTestimonial(t)}
                                                        disabled={saving}
                                                        className="bg-brand text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-[#c42e40] transition-colors flex items-center gap-2"
                                                    >
                                                        <Save className="w-4 h-4"/> Save Changes
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button onClick={addTestimonial} className="w-full py-8 border-2 border-dashed border-gray-300 rounded-2xl text-gray-400 hover:border-brand hover:text-brand hover:bg-brand/5 font-bold transition-all flex flex-col items-center gap-3 group">
                                        <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-brand/10 flex items-center justify-center transition-colors">
                                            <Plus className="w-6 h-6" />
                                        </div>
                                        Add New Testimonial
                                    </button>
                                </div>
                            )}

                            {/* --- BLOG TAB --- */}
                            {activeTab === 'blog' && (
                                <div className="space-y-8">

                                    {/* FORM: NEW / EDIT POST */}
                                    {editingPost && (
                                        <div className="bg-white p-8 rounded-3xl border border-brand/20 shadow-md space-y-5">
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="font-serif font-bold text-xl text-primary">
                                                    {editingPost.id ? 'Edit Post' : 'New Post'}
                                                </h3>
                                                <button onClick={() => { setEditingPost(null); setContentBlocks([]); }} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100">
                                                    <X className="w-5 h-5"/>
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                <div className="md:col-span-2">
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Title *</label>
                                                    <input
                                                        type="text"
                                                        value={editingPost.title}
                                                        onChange={e => setEditingPost({ ...editingPost, title: e.target.value })}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all font-bold text-lg"
                                                        placeholder="Article title..."
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Slug (URL)</label>
                                                    <input
                                                        type="text"
                                                        value={editingPost.slug}
                                                        onChange={e => setEditingPost({ ...editingPost, slug: e.target.value })}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all font-mono text-sm"
                                                        placeholder="my-article-slug"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Category</label>
                                                    <input
                                                        type="text"
                                                        value={editingPost.category}
                                                        onChange={e => setEditingPost({ ...editingPost, category: e.target.value })}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                        placeholder="Ex. Investment Guide"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Author</label>
                                                    <input
                                                        type="text"
                                                        value={editingPost.author}
                                                        onChange={e => setEditingPost({ ...editingPost, author: e.target.value })}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                        placeholder="Author name"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Cover Image</label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={editingPost.coverImageUrl}
                                                            onChange={e => setEditingPost({ ...editingPost, coverImageUrl: e.target.value })}
                                                            className="flex-1 p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                                            placeholder="https://... or upload below"
                                                        />
                                                        <label className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm cursor-pointer transition-all ${uploadingImage ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-600 hover:bg-brand/10 hover:text-brand'}`}>
                                                            {uploadingImage ? <Loader className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
                                                            {uploadingImage ? 'Uploading…' : 'Upload'}
                                                            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploadingImage}/>
                                                        </label>
                                                    </div>
                                                    {editingPost.coverImageUrl && (
                                                        <img src={editingPost.coverImageUrl} alt="preview" className="mt-2 h-24 w-full object-cover rounded-xl border border-gray-100" />
                                                    )}
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Excerpt (short description)</label>
                                                    <textarea
                                                        rows={2}
                                                        value={editingPost.excerpt}
                                                        onChange={e => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                                                        className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm resize-none"
                                                        placeholder="Short summary shown in the blog listing..."
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-[10px] font-bold uppercase text-gray-400 mb-2 block">Article Content</label>
                                                    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                                        <BlogBlockEditor
                                                            blocks={contentBlocks}
                                                            onChange={newBlocks => {
                                                                setContentBlocks(newBlocks);
                                                                setEditingPost({ ...editingPost, content: JSON.stringify(newBlocks) });
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                                <label className="flex items-center gap-3 cursor-pointer group">
                                                    <div
                                                        onClick={() => setEditingPost({ ...editingPost, published: !editingPost.published })}
                                                        className={`w-12 h-6 rounded-full transition-colors relative ${editingPost.published ? 'bg-green-500' : 'bg-gray-300'}`}
                                                    >
                                                        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${editingPost.published ? 'left-7' : 'left-1'}`}/>
                                                    </div>
                                                    <span className={`text-sm font-bold ${editingPost.published ? 'text-green-600' : 'text-gray-400'}`}>
                                                        {editingPost.published ? 'Published' : 'Draft'}
                                                    </span>
                                                </label>
                                                <button
                                                    onClick={() => saveBlogPost(editingPost)}
                                                    disabled={saving || !editingPost.title}
                                                    className="bg-brand text-white px-8 py-3 rounded-xl font-bold hover:bg-[#c42e40] transition-all shadow-md flex items-center gap-3 disabled:opacity-60"
                                                >
                                                    {saving ? <Loader className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                                                    Save Post
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* LIST OF POSTS */}
                                    <div className="space-y-4">
                                        {blogPosts.map(post => (
                                            <div key={post.id} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex gap-4 items-start group hover:border-brand/30 transition-colors">
                                                {post.coverImageUrl && (
                                                    <img src={post.coverImageUrl} alt={post.title} className="w-20 h-20 rounded-xl object-cover shrink-0 hidden md:block" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        {post.category && (
                                                            <span className="text-[10px] font-bold uppercase tracking-widest text-brand bg-brand/10 px-2 py-0.5 rounded-full">{post.category}</span>
                                                        )}
                                                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${post.published ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                                            {post.published ? 'Published' : 'Draft'}
                                                        </span>
                                                        {post.certificationTxId && (
                                                            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-green-50 text-green-700 flex items-center gap-1">
                                                                <ShieldCheck className="w-3 h-3"/> Certified
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h3 className="font-bold text-lg text-gray-900 truncate">{post.title}</h3>
                                                    <p className="text-sm text-gray-400 mt-0.5">{post.author} &mdash; {post.createdAt ? new Date(post.createdAt).toLocaleDateString('en-US', {year:'numeric',month:'short',day:'numeric'}) : ''}</p>
                                                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{post.excerpt}</p>
                                                    {post.certificationVerifyUrl && (
                                                        <a href={post.certificationVerifyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-green-600 font-medium mt-1 hover:underline">
                                                            <ExternalLink className="w-3 h-3"/> View certification
                                                        </a>
                                                    )}
                                                </div>
                                                <div className="flex flex-col gap-2 shrink-0">
                                                    <button onClick={() => { setEditingPost({ ...post }); setContentBlocks(parseContentToBlocks(post.content)); }} className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors" title="Edit">
                                                        <Edit3 className="w-4 h-4"/>
                                                    </button>
                                                    <button
                                                        onClick={() => handleCertifyPost(post.id)}
                                                        disabled={certifyingId === post.id}
                                                        className={`p-2 rounded-xl transition-colors ${post.certificationTxId ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-purple-50 text-purple-600 hover:bg-purple-100'}`}
                                                        title={post.certificationTxId ? 'Re-certify on blockchain' : 'Certify on blockchain'}
                                                    >
                                                        {certifyingId === post.id ? <Loader className="w-4 h-4 animate-spin"/> : <ShieldCheck className="w-4 h-4"/>}
                                                    </button>
                                                    <button onClick={() => deleteBlogPost(post.id)} className="p-2 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-colors" title="Delete">
                                                        <Trash2 className="w-4 h-4"/>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}

                                        {blogPosts.length === 0 && !editingPost && (
                                            <div className="text-center py-16 text-gray-400">
                                                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50"/>
                                                <p className="font-medium">No posts yet. Create your first article!</p>
                                            </div>
                                        )}
                                    </div>

                                    {!editingPost && (
                                        <button
                                            onClick={() => { setEditingPost({ ...EMPTY_POST }); setContentBlocks([{ id: genId(), type: 'paragraph', text: '', items: [] }]); }}
                                            className="w-full py-8 border-2 border-dashed border-gray-300 rounded-2xl text-gray-400 hover:border-brand hover:text-brand hover:bg-brand/5 font-bold transition-all flex flex-col items-center gap-3 group"
                                        >
                                            <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-brand/10 flex items-center justify-center transition-colors">
                                                <Plus className="w-6 h-6" />
                                            </div>
                                            Write New Article
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* --- MANUALS TAB --- */}
                            {activeTab === 'manuals' && editingManualId != null && (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
                                        <div>
                                            <button onClick={closeManualEditor} className="text-xs font-bold text-gray-500 hover:text-brand uppercase tracking-widest flex items-center gap-1 mb-1">
                                                <X className="w-3 h-3"/> Back to list
                                            </button>
                                            <h3 className="font-serif font-bold text-xl text-primary">Edit House Manual</h3>
                                        </div>
                                    </div>
                                    {editingManualLoading || !editingManual ? (
                                        <div className="flex items-center justify-center py-20 text-gray-400">
                                            <Loader className="w-8 h-8 animate-spin"/>
                                        </div>
                                    ) : (
                                        <ManualEditorForm
                                            manual={editingManual}
                                            setManual={setEditingManual as React.Dispatch<React.SetStateAction<HouseManual>>}
                                            onSave={saveEditingManual}
                                            saving={saving}
                                            uploadVideo={uploadEditingVideo}
                                            uploadImage={uploadEditingImage}
                                            onError={(msg) => setFeedback({ type: 'error', message: msg })}
                                        />
                                    )}
                                </div>
                            )}

                            {activeTab === 'manuals' && editingManualId == null && (
                                <div className="space-y-8">

                                    {/* CREATE NEW HOUSE */}
                                    <div className="bg-white p-8 rounded-3xl border border-brand/20 shadow-md">
                                        <h3 className="font-serif font-bold text-xl text-primary mb-5 flex items-center gap-3">
                                            <Plus className="w-5 h-5 text-brand"/> New House Manual
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">House Name *</label>
                                                <input
                                                    type="text"
                                                    value={newManual.houseName}
                                                    onChange={e => setNewManual({ ...newManual, houseName: e.target.value })}
                                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all font-bold"
                                                    placeholder="e.g., Bandello"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Badge / Code</label>
                                                <input
                                                    type="text"
                                                    value={newManual.badge}
                                                    onChange={e => setNewManual({ ...newManual, badge: e.target.value })}
                                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                    placeholder="e.g., AH-001"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Address *</label>
                                                <input
                                                    type="text"
                                                    value={newManual.address}
                                                    onChange={e => setNewManual({ ...newManual, address: e.target.value })}
                                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                    placeholder="e.g., Via Bandello 22, Milano"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Owner Password * <span className="text-gray-400 normal-case font-normal">(min 4 chars — share with the property owner)</span></label>
                                                <input
                                                    type="text"
                                                    value={newManual.ownerPassword}
                                                    onChange={e => setNewManual({ ...newManual, ownerPassword: e.target.value })}
                                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all font-mono"
                                                    placeholder="A unique password for this owner only"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-end mt-5">
                                            <button
                                                onClick={createManual}
                                                disabled={creatingManual}
                                                className="bg-brand text-white px-8 py-3 rounded-xl font-bold hover:bg-[#c42e40] transition-all shadow-md flex items-center gap-3 disabled:opacity-60"
                                            >
                                                {creatingManual ? <Loader className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
                                                Create House Manual
                                            </button>
                                        </div>
                                    </div>

                                    {/* LIST */}
                                    <div className="space-y-4">
                                        {manuals.map(m => (
                                            <div key={m.id} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                                                <div className="flex justify-between items-start mb-4 gap-4 flex-wrap">
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                            <h3 className="font-bold text-lg text-gray-900">{m.houseName}</h3>
                                                            {m.badge && (
                                                                <span className="text-[10px] font-bold uppercase tracking-widest text-brand bg-brand/10 px-2 py-0.5 rounded-full">{m.badge}</span>
                                                            )}
                                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                                {m.itemsCount} {m.itemsCount === 1 ? 'item' : 'items'}
                                                            </span>
                                                        </div>
                                                        <p className="text-sm text-gray-500">{m.address}</p>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Created {m.createdAt ? new Date(m.createdAt).toLocaleDateString() : ''}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-2 shrink-0">
                                                        <button onClick={() => openManualEditor(m.id)} className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors" title="Edit content">
                                                            <Edit3 className="w-4 h-4"/>
                                                        </button>
                                                        <button onClick={() => setResetPwdId(resetPwdId === m.id ? null : m.id)} className="p-2 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-100 transition-colors" title="Reset owner password">
                                                            <KeyRound className="w-4 h-4"/>
                                                        </button>
                                                        <button onClick={() => regenerateSlugs(m.id)} className="p-2 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-100 transition-colors" title="Regenerate links (invalidate old ones)">
                                                            <RefreshCw className="w-4 h-4"/>
                                                        </button>
                                                        <button onClick={() => deleteManualById(m.id)} className="p-2 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-colors" title="Delete">
                                                            <Trash2 className="w-4 h-4"/>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {/* GUEST LINK */}
                                                    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1">
                                                                <Eye className="w-3 h-3"/> Guest Link (paste in Guesty)
                                                            </span>
                                                            <button
                                                                onClick={() => copyToClipboard(buildGuestUrl(m.guestSlug), `g-${m.id}`)}
                                                                className="text-xs font-bold text-brand hover:bg-brand/10 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
                                                            >
                                                                {copiedKey === `g-${m.id}` ? <><CheckCircle className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
                                                            </button>
                                                        </div>
                                                        <p className="text-xs font-mono text-gray-700 break-all leading-snug">{buildGuestUrl(m.guestSlug)}</p>
                                                        <a href={buildGuestUrl(m.guestSlug)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-brand font-medium mt-2 hover:underline">
                                                            <ExternalLink className="w-3 h-3"/> Preview
                                                        </a>
                                                    </div>

                                                    {/* OWNER LINK */}
                                                    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1">
                                                                <Edit3 className="w-3 h-3"/> Owner Edit Link
                                                            </span>
                                                            <button
                                                                onClick={() => copyToClipboard(buildEditUrl(m.editSlug), `e-${m.id}`)}
                                                                className="text-xs font-bold text-brand hover:bg-brand/10 px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
                                                            >
                                                                {copiedKey === `e-${m.id}` ? <><CheckCircle className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
                                                            </button>
                                                        </div>
                                                        <p className="text-xs font-mono text-gray-700 break-all leading-snug">{buildEditUrl(m.editSlug)}</p>
                                                        <p className="text-[10px] text-gray-400 mt-2 italic">Send this link + owner password to the property owner.</p>
                                                    </div>
                                                </div>

                                                {/* RESET PASSWORD INLINE */}
                                                {resetPwdId === m.id && (
                                                    <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2 items-center">
                                                        <input
                                                            type="text"
                                                            value={resetPwdValue}
                                                            onChange={e => setResetPwdValue(e.target.value)}
                                                            placeholder="New owner password (min 4 chars)"
                                                            className="flex-1 p-3 bg-gray-50 rounded-xl border border-gray-200 focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none text-sm font-mono"
                                                            autoFocus
                                                        />
                                                        <button
                                                            onClick={() => submitResetPassword(m.id)}
                                                            className="bg-amber-500 text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-amber-600 transition-colors flex items-center gap-2"
                                                        >
                                                            <Save className="w-4 h-4"/> Save
                                                        </button>
                                                        <button
                                                            onClick={() => { setResetPwdId(null); setResetPwdValue(''); }}
                                                            className="text-gray-400 hover:text-gray-600 p-2"
                                                        >
                                                            <X className="w-5 h-5"/>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {manuals.length === 0 && (
                                            <div className="text-center py-16 text-gray-400 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                                                <BookText className="w-12 h-12 mx-auto mb-4 opacity-50"/>
                                                <p className="font-medium">No house manuals yet. Create your first one above.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* --- SECURITY TAB --- */}
                            {activeTab === 'security' && (
                                <div className="bg-white p-10 rounded-3xl shadow-sm border border-gray-200 max-w-2xl mx-auto">
                                    <h3 className="font-serif font-bold text-2xl text-primary mb-6 flex items-center gap-3">
                                        <Lock className="w-6 h-6 text-brand"/> Update Account Password
                                    </h3>
                                    <form onSubmit={submitPasswordChange} className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Current Password</label>
                                            <input
                                                type="password"
                                                name="currentPassword"
                                                value={passwordData.currentPassword}
                                                onChange={handlePasswordChange}
                                                required
                                                className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                placeholder="••••••••"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2">New Password</label>
                                                <input
                                                    type="password"
                                                    name="newPassword"
                                                    value={passwordData.newPassword}
                                                    onChange={handlePasswordChange}
                                                    required
                                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                    placeholder="Minimum 8 characters"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2">Confirm New Password</label>
                                                <input
                                                    type="password"
                                                    name="confirmPassword"
                                                    value={passwordData.confirmPassword}
                                                    onChange={handlePasswordChange}
                                                    required
                                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                    placeholder="Repeat the new password"
                                                />
                                            </div>
                                        </div>

                                        <div className="pt-4 flex justify-end">
                                            <button
                                                type="submit"
                                                disabled={saving}
                                                className="bg-brand text-white px-8 py-3 rounded-xl font-bold hover:bg-[#c42e40] transition-all shadow-md hover:shadow-lg flex items-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                                            >
                                                {saving ? <Loader className="w-5 h-5 animate-spin"/> : 'Update Password Now'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {/* --- OWNER ONBOARDING TAB --- */}
                            {activeTab === 'onboarding' && (
                                <OnboardingAdminPanel />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;